import { readEnv } from '../../config/env';
import { CONNECTOR_BASE_URL } from '../config';
import {
  ApiResponse,
  DatasetExportRequest,
  DatasetExportResponse,
  DatasetExportPreviewData,
  DatasetImportJob,
  DatasetContentResponse,
  DatasetSourceUsersData,
  DatasetSourceDialogsData,
} from '../../types/api';
import type {
  ConnectorIntegrationRecord,
  ConnectorIntegrationConfigEnvelope,
  ConnectorIntegrationConfigResponse,
  ConnectorIntegrationListResponse,
  ConnectorIntegrationUsersResponse,
} from '../../types/connector';
import type {
  WorkflowReadOptions,
  WorkflowReadResult,
  WorkflowRollbackRequest,
  WorkflowRollbackResult,
  WorkflowSaveRequest,
  WorkflowSaveResponse,
  WorkflowSnapshot,
} from '../../types/workflow';
import { api, createApiClient, unwrapApiData } from './client';

const connectorApi = createApiClient(CONNECTOR_BASE_URL);

/** AIT-327: стенд не пропустил If-None-Match через CORS — до перезагрузки страницы читаем граф обычным GET. */
let conditionalWorkflowReadBlocked = false;

/** Для тестов: вернуть условное чтение графа. */
export function resetConditionalWorkflowRead(): void {
  conditionalWorkflowReadBlocked = false;
}

/** Ошибка сети без ответа сервера — так axios видит запрос, отбитый CORS. */
function isNetworkErrorWithoutResponse(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { response?: unknown; isAxiosError?: boolean; code?: string; message?: string };
  if (e.response) return false;
  return e.isAxiosError === true || e.code === 'ERR_NETWORK' || e.message === 'Network Error';
}

/**
 * Headers for the connector's internal contour (dev-only workflow test chat).
 * The client never carries a built-in service key (AIT-397): the key comes only from
 * the dev build environment, and without it the header is simply not sent, so the
 * backend decides by the user's own session. Every caller is gated on
 * `workflowTestChatEnabled()`, so this never reaches a qa or prod build.
 */
function internalContourHeaders(): Record<string, string> {
  const token = readEnv('REACT_APP_CONNECTOR_SERVICE_TOKEN');
  return token ? { 'X-Api-Key': token } : {};
}

// Connector endpoints (new)
export const connector = {
  // Admin: list all integrations
  listIntegrationsAdmin: async (): Promise<ConnectorIntegrationListResponse> => {
    const response = await connectorApi.get<ConnectorIntegrationListResponse>('/connector/admin/integrations');
    return response.data;
  },

  // Admin: patch integration fields (e.g. url)
  patchIntegrationAdmin: async (integrationId: string, data: Record<string, any>): Promise<ApiResponse<unknown>> => {
    const response = await connectorApi.patch(`/connector/admin/integrations/${integrationId}`, data);
    return response.data;
  },

  // Admin: delete integration
  deleteIntegrationAdmin: async (integrationId: string): Promise<ApiResponse<unknown>> => {
    const response = await connectorApi.delete(`/connector/admin/integrations/${integrationId}`);
    return response.data;
  },

  // Admin: assign user to integration
  assignUserToIntegration: async (integrationId: string, user_id: string): Promise<ApiResponse<unknown>> => {
    const response = await connectorApi.post(`/connector/admin/integrations/${integrationId}/users`, { user_id });
    return response.data;
  },

  // Admin: list users in integration
  listUsersInIntegration: async (integrationId: string): Promise<ConnectorIntegrationUsersResponse> => {
    const response = await connectorApi.get<ConnectorIntegrationUsersResponse>(
      `/connector/admin/integrations/${integrationId}/users`
    );
    return response.data;
  },

  // User/control: get integration config
  getIntegrationConfig: async (integrationId: string): Promise<ConnectorIntegrationConfigResponse> => {
    const response = await connectorApi.get<ConnectorIntegrationConfigResponse>(
      `/connector/integrations/control/${integrationId}/config`,
      {
        // Prevent stale config reads when config was changed externally.
        params: { _t: Date.now() },
      }
    );
    return response.data;
  },

  // User/control: set integration config
  setIntegrationConfig: async (
    integrationId: string,
    body: ConnectorIntegrationConfigEnvelope
  ): Promise<ApiResponse<unknown>> => {
    const response = await connectorApi.post(`/connector/integrations/control/${integrationId}/config`, body);
    return response.data;
  },

  // ─── Target-system workflow (docs/WORKFLOW_API.md) ──────────────────────
  // These three endpoints return their body as-is: there is NO { success, data }
  // envelope. Auth is the `access_token` cookie (already sent via withCredentials).

  /**
   * GET …/workflow — read the workflow snapshot (graph, versions, component
   * library, …). Pass the quoted ETag from a previous read to get a 304 with an
   * empty body when nothing changed. Deliberately NO `_t` cache-buster: freshness
   * comes from the digest, a unique URL would defeat the conditional GET and pull
   * ~1.5 MB on every render.
   */
  getWorkflow: async (integrationId: string, opts: WorkflowReadOptions = {}): Promise<WorkflowReadResult> => {
    const params: Record<string, string> = {};
    if (opts.include && opts.include.length) params.include = opts.include.join(',');
    if (opts.includeArchived) params.include_archived = 'true';
    if (opts.stand) params.stand = opts.stand;
    const url = `/connector/integrations/control/${encodeURIComponent(integrationId)}/workflow`;
    const read = (etag: string | null | undefined) =>
      connectorApi.get<WorkflowSnapshot>(url, {
        params,
        headers: etag ? { 'If-None-Match': etag } : undefined,
        validateStatus: (status) => status === 200 || status === 304,
      });
    const conditionalEtag = conditionalWorkflowReadBlocked ? null : (opts.etag ?? null);
    let response;
    try {
      response = await read(conditionalEtag);
    } catch (error) {
      // AIT-327: If-None-Match — «непростой» заголовок, ему нужен preflight. Если стенд не
      // разрешает его в Access-Control-Allow-Headers, браузер рубит запрос целиком (axios видит
      // Network Error без ответа) — и Refresh ломал загрузку графа. Повторяем обычным GET и до
      // конца сессии условный запрос не шлём: тело придёт целиком, свежесть видна по digest.
      if (!conditionalEtag || !isNetworkErrorWithoutResponse(error)) throw error;
      conditionalWorkflowReadBlocked = true;
      response = await read(null);
    }
    if (response.status === 304) return { status: 304 };
    const headers = (response.headers || {}) as Record<string, unknown>;
    const rawEtag = headers.etag ?? headers.ETag;
    const digest = response.data?.revision?.digest;
    // The header may not be exposed through CORS — it always equals the quoted digest.
    const etag = typeof rawEtag === 'string' && rawEtag ? rawEtag : digest ? `"${digest}"` : null;
    return { status: 200, snapshot: response.data, etag };
  },

  /**
   * POST …/workflow — dry run (plan with diff) or real apply, depending on
   * `body.dry_run`. Admin only. 409 when `base_revision` is stale.
   */
  saveWorkflow: async (integrationId: string, body: WorkflowSaveRequest): Promise<WorkflowSaveResponse> => {
    const response = await connectorApi.post<WorkflowSaveResponse>(
      `/connector/integrations/control/${encodeURIComponent(integrationId)}/workflow`,
      body
    );
    return response.data;
  },

  /** POST …/workflow/rollback — roll back to a `version_id` from `workflow_versions`. Admin only. */
  rollbackWorkflow: async (integrationId: string, body: WorkflowRollbackRequest): Promise<WorkflowRollbackResult> => {
    const response = await connectorApi.post<WorkflowRollbackResult>(
      `/connector/integrations/control/${encodeURIComponent(integrationId)}/workflow/rollback`,
      body
    );
    return response.data;
  },

  // User/control: add integration
  addIntegration: async (data: { url: string; apiKey?: string }): Promise<ApiResponse<unknown>> => {
    const response = await connectorApi.post('/connector/integrations/control', data);
    return response.data;
  },

  // User/control: list my integrations
  listMyIntegrations: async (): Promise<ConnectorIntegrationListResponse> => {
    const response = await connectorApi.get<ConnectorIntegrationListResponse>('/connector/integrations/control');
    return response.data;
  },

  // User/control: get integration
  /** The integration record; the stand answers both with `{ success, data }` and bare (AIT-425). */
  getIntegration: async (integrationId: string): Promise<ConnectorIntegrationRecord | null> => {
    const response = await connectorApi.get<ApiResponse<ConnectorIntegrationRecord> | ConnectorIntegrationRecord>(
      `/connector/integrations/control/${integrationId}`
    );
    return unwrapApiData<ConnectorIntegrationRecord>(response.data);
  },

  // User/control: patch integration
  patchIntegration: async (integrationId: string, data: Record<string, any>): Promise<ApiResponse<unknown>> => {
    const response = await connectorApi.patch(`/connector/integrations/control/${integrationId}`, data);
    return response.data;
  },

  // User/control: delete integration
  deleteIntegration: async (integrationId: string): Promise<ApiResponse<unknown>> => {
    const response = await connectorApi.delete(`/connector/integrations/control/${integrationId}`);
    return response.data;
  },

  // Get datasets for integration
  getDatasets: async (integrationId: string): Promise<ApiResponse<import('../../types/api').ConnectorDataset[]>> => {
    const response = await connectorApi.get<ApiResponse<import('../../types/api').ConnectorDataset[]>>(
      '/connector/datasets',
      { params: { integration_id: integrationId } }
    );
    return response.data;
  },

  // Unified dataset content viewer endpoint for all dataset sources/types.
  getDatasetContent: async (datasetId: string, integrationId: string): Promise<ApiResponse<DatasetContentResponse>> => {
    const response = await api.get<ApiResponse<DatasetContentResponse>>(
      `/training/datasets/${encodeURIComponent(datasetId)}/content`,
      { params: { integration_id: integrationId } }
    );
    return response.data;
  },

  /**
   * Internal contour — service-token gated (AIT-121), NOT reachable with a user session.
   * Everything below is behind `REACT_APP_WORKFLOW_TEST_CHAT`, which exists only in
   * `.env.dev`: the key falls back to a placeholder, and shipping a service token in a
   * browser bundle would hand the whole internal contour to anyone with devtools.
   *
   * Passing `state` + `routing` switches the product to a full production turn on that
   * position instead of a shortened one (AIT-126) — that is what makes a before/after
   * run on an unapplied workflow possible at all.
   */
  replyDialogWithConfig: async (
    integrationId: string,
    body: {
      config: string;
      dialog: Array<{ type: 'human' | 'ai' | 'system'; content: string }>;
      /** Seat identity. Required together with `state`/`routing`. */
      user_id?: string;
      /** State definition to run the turn on: prompts, agent roster, triggers, map_config. */
      state?: unknown;
      /** Must travel with `state` — the pair is what the actor validates. */
      routing?: unknown;
      rag_enabled?: boolean;
      ltm_enabled?: boolean;
      diary_enabled?: boolean;
      portrait_enabled?: boolean;
      chat_id?: string;
    }
  ): Promise<ApiResponse<unknown>> => {
    const response = await connectorApi.post(
      `/connector/internal/integrations/${integrationId}/reply-dialog-with-config`,
      body,
      { headers: internalContourHeaders() }
    );
    return response.data;
  },

  /**
   * A clean identity plus the `state`/`routing` it starts on. Isolation is per seat, not
   * per chat_id: two candidates on one identity would differ by launch order rather than
   * by config, because the Map, LTM, diary and portrait are written for real (AIT-126).
   */
  takeEphemeralSeat: async (integrationId: string): Promise<ApiResponse<unknown>> => {
    const response = await connectorApi.post(
      `/connector/internal/integrations/${integrationId}/ephemeral/seats`,
      {},
      { headers: internalContourHeaders() }
    );
    return response.data;
  },

  releaseEphemeralSeat: async (integrationId: string, seatId: string): Promise<void> => {
    await connectorApi.delete(
      `/connector/internal/integrations/${integrationId}/ephemeral/seats/${encodeURIComponent(seatId)}`,
      { headers: internalContourHeaders() }
    );
  },

  // Training jobs
  createTrainingJob: async (
    data: import('../../types/api').TrainingJobRequestBody,
    options?: { signal?: AbortSignal }
  ): Promise<ApiResponse<any>> => {
    console.log('📤 Creating training job with payload:', JSON.stringify(data, null, 2));
    // Runtime axios supports AbortSignal; legacy @types/axios may omit `signal` on config — use cast.
    const response = await api.post<ApiResponse<any>>(
      '/training/jobs',
      data,
      options?.signal ? ({ signal: options.signal } as any) : undefined
    );
    console.log('✅ Training job created successfully:', response.data);
    return response.data;
  },

  // Preview before creating training job
  previewTrainingJob: async (
    data: import('../../types/api').TrainingJobRequestBody,
    options?: { signal?: AbortSignal }
  ): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>(
      '/training/jobs/preview',
      data,
      options?.signal ? ({ signal: options.signal } as any) : undefined
    );
    return response.data;
  },

  /** Agents found in saved datasets (continuous / historical config training). */
  previewDatasetAgents: async (
    data: import('../../types/api').DatasetAgentsPreviewRequest,
    options?: { signal?: AbortSignal }
  ): Promise<ApiResponse<import('../../types/api').DatasetAgentsPreviewResponse>> => {
    const response = await api.post<ApiResponse<import('../../types/api').DatasetAgentsPreviewResponse>>(
      '/training/datasets/agents/preview',
      data,
      options?.signal ? ({ signal: options.signal } as any) : undefined
    );
    return response.data;
  },

  // Poll training job status (integration_id required in query per API)
  pollTrainingJob: async (jobId: string, integrationId: string): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>(
      `/training/jobs/${jobId}/poll`,
      {},
      { params: { integration_id: integrationId } }
    );
    return response.data;
  },

  cancelTrainingJob: async (jobId: string, integrationId: string): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>(
      `/training/jobs/${encodeURIComponent(jobId)}/cancel`,
      {},
      { params: { integration_id: integrationId } }
    );
    return response.data;
  },

  /** Creates a new job from a failed/cancelled/partially_succeeded job; returns the new job payload. */
  retryTrainingJob: async (
    jobId: string,
    integrationId: string,
    options?: { signal?: AbortSignal }
  ): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>(`/training/jobs/${encodeURIComponent(jobId)}/retry`, {}, {
      params: { integration_id: integrationId },
      timeout: 90_000,
      ...(options?.signal ? { signal: options.signal } : {}),
    } as any);
    return response.data;
  },

  // Get training job by ID (integration_id required in query)
  getTrainingJob: async (jobId: string, integrationId: string): Promise<ApiResponse<any>> => {
    const response = await api.get<ApiResponse<any>>(`/training/jobs/${jobId}`, {
      params: { integration_id: integrationId },
    });
    return response.data;
  },

  // List all training jobs (integration_id required)
  listTrainingJobs: async (params: {
    integration_id: string;
    limit?: number;
    offset?: number;
  }): Promise<
    ApiResponse<{
      items: any[];
      pagination: { limit: number; offset: number; total: number };
    }>
  > => {
    const response = await api.get<
      ApiResponse<{
        items: any[];
        pagination: { limit: number; offset: number; total: number };
      }>
    >('/training/jobs', { params });
    return response.data;
  },

  // List fine-tuning history rows (new endpoint)
  listTrainingHistory: async (params?: {
    limit?: number;
    offset?: number;
  }): Promise<
    ApiResponse<{
      items: any[];
      pagination: { limit: number; offset: number; total: number };
    }>
  > => {
    const response = await api.get<
      ApiResponse<{
        items: any[];
        pagination: { limit: number; offset: number; total: number };
      }>
    >('/training/history', { params });
    return response.data;
  },

  // List agents for an integration
  listIntegrationAgents: async (integrationId: string): Promise<ApiResponse<unknown>> => {
    const response = await api.get<ApiResponse<unknown>>(`/training/integrations/${integrationId}/agents`);
    return response.data;
  },

  /** DELETE /training/agents/{agent_id}/model — removes fine-tuned model linked to training agent */
  deleteTrainingAgentModel: async (agentId: string): Promise<ApiResponse<unknown>> => {
    const response = await api.delete<ApiResponse<unknown>>(`/training/agents/${encodeURIComponent(agentId)}/model`);
    return response.data;
  },

  // Auto Fine-Tuning configs
  createAutoConfig: async (data: {
    integration_id: string;
    enabled: boolean;
    schedule: string;
    regime: string;
    regime_percent: number;
    period_days: number;
    history_depth?: number;
    model_name_prefix: string;
    agents: Array<{
      agent_id: string;
      agent_name: string;
      dataset_id?: string;
      base_model?: string;
      is_trainable: boolean;
      filters?: { reactions?: string[]; comment?: boolean; groundtruth?: boolean };
    }>;
  }): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>('/training/auto/configs', data);
    return response.data;
  },

  listAutoConfigs: async (params: { integration_id: string }): Promise<ApiResponse<any>> => {
    const response = await api.get<ApiResponse<any>>('/training/auto/configs', { params });
    return response.data;
  },

  getAutoConfig: async (configId: string, integrationId: string): Promise<ApiResponse<any>> => {
    const response = await api.get<ApiResponse<any>>(`/training/auto/configs/${configId}`, {
      params: { integration_id: integrationId },
    });
    return response.data;
  },

  updateAutoConfig: async (
    configId: string,
    integrationId: string,
    data: { enabled: boolean }
  ): Promise<ApiResponse<any>> => {
    const response = await api.patch<ApiResponse<any>>(`/training/auto/configs/${configId}`, data, {
      params: { integration_id: integrationId },
    });
    return response.data;
  },

  /** Full update of auto config (PUT) */
  putAutoConfig: async (
    configId: string,
    integrationId: string,
    data: {
      enabled: boolean;
      schedule: string;
      regime: string;
      regime_percent: number;
      period_days: number;
      history_depth?: number;
      model_name_prefix: string;
      agents: Array<{
        agent_id: string;
        agent_name: string;
        dataset_id?: string;
        is_trainable: boolean;
        base_model?: string;
        filters?: { reactions?: string[]; comment?: boolean; groundtruth?: boolean };
      }>;
    }
  ): Promise<ApiResponse<any>> => {
    const response = await api.put<ApiResponse<any>>(`/training/auto/configs/${configId}`, data, {
      params: { integration_id: integrationId },
    });
    return response.data;
  },

  deleteAutoConfig: async (configId: string, integrationId: string): Promise<ApiResponse<any>> => {
    const response = await api.delete<ApiResponse<any>>(`/training/auto/configs/${configId}`, {
      params: { integration_id: integrationId },
    });
    return response.data;
  },

  getAutoConfigStatus: async (configId: string, integrationId: string): Promise<ApiResponse<any>> => {
    const response = await api.get<ApiResponse<any>>(`/training/auto/configs/${configId}/status`, {
      params: { integration_id: integrationId },
    });
    return response.data;
  },

  runAutoConfig: async (configId: string, integrationId: string): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>(
      `/training/auto/configs/${configId}/run`,
      {},
      { params: { integration_id: integrationId } }
    );
    return response.data;
  },

  // Get model lineage graph
  getModelLineage: async (params: {
    integration_id: string;
    view?: string;
    compact?: boolean;
    latest_per_alias?: boolean;
    limit_nodes?: number;
    limit_edges?: number;
  }): Promise<ApiResponse<any>> => {
    const response = await api.get<ApiResponse<any>>('/training/models/lineage', { params });
    return response.data;
  },

  // List trainable models for an integration (for Profile → Models tab and configurator)
  listTrainableModels: async (params: {
    integration_id: string;
    agent_id?: string;
    agent_name?: string;
    status?: string;
    succeeded_only?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<
    ApiResponse<{
      items: import('../../types/api').TrainableModelItem[];
      pagination?: { limit: number; offset: number; total: number };
    }>
  > => {
    const response = await api.get<ApiResponse<any>>('/training/models/trainable', { params });
    return response.data;
  },

  /** Export dataset for training (period + filters). Returns dataset_meta (id, label, inner_label, s3_key), train/validation. */
  exportDataset: async (
    data: DatasetExportRequest,
    options?: { signal?: AbortSignal }
  ): Promise<ApiResponse<DatasetExportResponse>> => {
    const response = await api.post<ApiResponse<DatasetExportResponse>>(
      '/training/datasets/export',
      data,
      options?.signal ? ({ signal: options.signal } as any) : undefined
    );
    return response.data;
  },

  /** Start async dataset import job (same body as export). */
  createDatasetImportJob: async (
    data: DatasetExportRequest,
    options?: { signal?: AbortSignal }
  ): Promise<ApiResponse<DatasetImportJob>> => {
    const response = await api.post<ApiResponse<DatasetImportJob>>(
      '/training/datasets/import-jobs',
      data,
      options?.signal ? ({ signal: options.signal } as any) : undefined
    );
    return response.data;
  },

  /** Poll async dataset import job status. */
  getDatasetImportJob: async (jobId: string): Promise<ApiResponse<DatasetImportJob>> => {
    const response = await api.get<ApiResponse<DatasetImportJob>>(`/training/datasets/import-jobs/${jobId}`);
    return response.data;
  },

  /** Cancel a queued or running dataset import job. */
  cancelDatasetImportJob: async (jobId: string): Promise<ApiResponse<DatasetImportJob>> => {
    const response = await api.post<ApiResponse<DatasetImportJob>>(
      `/training/datasets/import-jobs/${encodeURIComponent(jobId)}/cancel`
    );
    return response.data;
  },

  /** Preview export: token totals (POST /training/datasets/export/preview). */
  exportDatasetPreview: async (
    data: DatasetExportRequest,
    options?: { signal?: AbortSignal }
  ): Promise<ApiResponse<DatasetExportPreviewData>> => {
    const response = await api.post<ApiResponse<DatasetExportPreviewData>>(
      '/training/datasets/export/preview',
      data,
      options?.signal ? ({ signal: options.signal } as any) : undefined
    );
    return response.data;
  },

  /** Consented Virtual Psychologist users for import filters (GET /training/datasets/source-users). */
  listDatasetSourceUsers: async (
    integrationId: string,
    opts?: { from?: string; to?: string }
  ): Promise<ApiResponse<DatasetSourceUsersData>> => {
    const response = await api.get<ApiResponse<DatasetSourceUsersData>>('/training/datasets/source-users', {
      params: {
        integration_id: integrationId,
        ...(opts?.from ? { from: opts.from } : {}),
        ...(opts?.to ? { to: opts.to } : {}),
      },
    });
    return response.data;
  },

  /** Source dialogs for import filters (GET /training/datasets/source-dialogs). */
  listDatasetSourceDialogs: async (
    integrationId: string,
    opts: {
      page: number;
      limit?: number;
      user_ids?: string[];
      dialog_ids?: string[];
      from?: string;
      to?: string;
    }
  ): Promise<ApiResponse<DatasetSourceDialogsData>> => {
    const params: Record<string, string | number> = {
      integration_id: integrationId,
      page: opts.page,
      limit: opts.limit ?? 50,
    };
    if (opts.from) params.from = opts.from;
    if (opts.to) params.to = opts.to;
    if (opts.user_ids && opts.user_ids.length > 0) params.user_ids = opts.user_ids.join(',');
    if (opts.dialog_ids && opts.dialog_ids.length > 0) params.dialog_ids = opts.dialog_ids.join(',');
    const response = await api.get<ApiResponse<DatasetSourceDialogsData>>('/training/datasets/source-dialogs', {
      params,
    });
    return response.data;
  },
};
